import axios from 'axios';
import { apiFetch, useBackendAuth } from './apiClient';
import { stripeHeaders } from './stripeApi';
import type { AppSettings } from '../settings/types';

const STRIPE_V1 = 'https://api.stripe.com/v1';

export type OpenInvoice = {
  id: string;
  status: string | null;
  number?: string | null;
  currency: string;
  total: number;
  amount_due: number;
  amount_paid: number;
  created: number;
  due_date?: number | null;
  hosted_invoice_url?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
};

async function stripePostForm<T>(secretKey: string, path: string, body: Record<string, string | number | boolean>): Promise<T> {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(body)) {
    usp.append(k, String(v));
  }
  const { data } = await axios.post<T>(`${STRIPE_V1}${path}`, usp, {
    headers: { ...stripeHeaders(secretKey), 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return data;
}

export async function fetchOpenInvoices(settings: AppSettings): Promise<OpenInvoice[]> {
  if (useBackendAuth) {
    const r = await apiFetch<{ invoices: OpenInvoice[] }>('/api/billing/invoices/open');
    return r.invoices;
  }
  const key = settings.integrations.stripe.secretKey.trim();
  if (!key) {
    return [];
  }
  const { data } = await axios.get<{ data: OpenInvoice[] }>(`${STRIPE_V1}/invoices`, {
    headers: stripeHeaders(key),
    params: { status: 'open', limit: 50, 'expand[]': 'data.customer' },
  });
  return data.data.map((inv) => {
    const cust = inv as OpenInvoice & { customer?: { email?: string; name?: string } };
    return {
      ...inv,
      customer_email: cust.customer?.email ?? inv.customer_email,
      customer_name: cust.customer?.name ?? inv.customer_name,
    };
  });
}

export async function sendInvoiceReminder(settings: AppSettings, invoiceId: string): Promise<void> {
  if (useBackendAuth) {
    await apiFetch(`/api/billing/reminders/${invoiceId}`, { method: 'POST' });
    return;
  }
  const key = settings.integrations.stripe.secretKey.trim();
  await stripePostForm(key, `/invoices/${invoiceId}/send`, {});
}

export async function runAllReminders(): Promise<{ sent: number; skipped: number }> {
  return apiFetch('/api/billing/reminders/run', { method: 'POST' });
}

export async function createRecurringSubscription(
  settings: AppSettings,
  customerId: string,
  priceId: string
): Promise<{ id: string }> {
  if (useBackendAuth) {
    const r = await apiFetch<{ subscription: { id: string } }>('/api/billing/subscriptions/create', {
      method: 'POST',
      body: JSON.stringify({ customerId, priceId }),
    });
    return r.subscription;
  }
  const key = settings.integrations.stripe.secretKey.trim();
  const billing = settings.integrations.stripe.billing;
  return stripePostForm(key, '/subscriptions', {
    customer: customerId,
    'items[0][price]': priceId,
    collection_method: 'send_invoice',
    days_until_due: billing.daysUntilDue,
  });
}

export async function createAndSendInvoice(
  settings: AppSettings,
  opts: { customerId: string; amountCents: number; description: string; currency?: string }
): Promise<{ id: string }> {
  if (useBackendAuth) {
    const r = await apiFetch<{ invoice: { id: string } }>('/api/billing/invoices/create', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
    return r.invoice;
  }
  const key = settings.integrations.stripe.secretKey.trim();
  const billing = settings.integrations.stripe.billing;
  const cur = (opts.currency || billing.defaultCurrency || 'aud').toLowerCase();
  const inv = await stripePostForm<{ id: string }>(key, '/invoices', {
    customer: opts.customerId,
    collection_method: 'send_invoice',
    days_until_due: billing.daysUntilDue,
    auto_advance: false,
  });
  await stripePostForm(key, '/invoiceitems', {
    invoice: inv.id,
    amount: opts.amountCents,
    currency: cur,
    description: opts.description,
  });
  const finalized = await stripePostForm<{ id: string }>(key, `/invoices/${inv.id}/finalize`, {});
  if (billing.autoSendInvoices) {
    await stripePostForm(key, `/invoices/${finalized.id}/send`, {});
  }
  return finalized;
}

export async function listStripePrices(settings: AppSettings): Promise<
  Array<{ id: string; nickname: string; amount: number; currency: string; interval: string }>
> {
  type PriceRow = {
    id: string;
    unit_amount: number | null;
    currency: string;
    nickname?: string | null;
    recurring?: { interval: string } | null;
  };

  const mapPrices = (rows: PriceRow[]) =>
    rows
      .filter((p) => p.unit_amount != null)
      .map((p) => ({
        id: p.id,
        nickname: p.nickname || p.id,
        amount: p.unit_amount!,
        currency: p.currency,
        interval: p.recurring?.interval || 'month',
      }));

  if (useBackendAuth) {
    const data = await apiFetch<{ data: PriceRow[] }>(
      '/api/stripe/request?path=/prices&limit=50&active=true&type=recurring'
    );
    return mapPrices(data.data || []);
  }

  const key = settings.integrations.stripe.secretKey.trim();
  if (!key) {
    return [];
  }
  const { data } = await axios.get<{ data: PriceRow[] }>(`${STRIPE_V1}/prices`, {
    headers: stripeHeaders(key),
    params: { limit: 50, active: true, type: 'recurring' },
  });
  return mapPrices(data.data);
}
